import logoUrl from '../assets/logo.png'

/** Product name for shared docs / marketing chrome. */
export const SCENARIO_TOOL_NAME = 'Buxus Scenario Studio'
/** Shorter in-app / portal label. */
export const SCENARIO_TOOL_SHORT_NAME = 'Scenario Studio'

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function logoSrc(): string {
  try {
    return new URL(logoUrl, window.location.href).href
  } catch {
    return logoUrl
  }
}

/**
 * Content revision date for the Trainer Guide PDF footer.
 * Bump when the guide body meaningfully changes — not generate/print time.
 */
export const TRAINER_GUIDE_UPDATED = 'Oct 22, 2026'

function docHeaderHtml(docKind: string): string {
  return `
    <header class="guide-header">
      <img src="${escapeHtml(logoSrc())}" alt="Buxus" />
      <div class="guide-header-text">
        <h1>${escapeHtml(SCENARIO_TOOL_NAME)} — ${escapeHtml(docKind)}</h1>
      </div>
    </header>`
}

/** ASCII-safe title for print/save-as filenames (no em dashes or other punctuation). */
function pdfFileTitle(docKind: string): string {
  const raw = `${SCENARIO_TOOL_NAME} - ${docKind}`
  return raw.replace(/[^\w\s.-]/g, '').replace(/\s+/g, ' ').trim()
}

function docFooterHtml(updated: string): string {
  return `<footer class="guide-footer">Updated ${escapeHtml(updated)}</footer>`
}

const PRINT_CSS = `
  @page { size: letter; margin: 0.5in; }
  html, body {
    margin: 0;
    padding: 0;
  }
  body {
    font: 13.5px/1.5 "Segoe UI", system-ui, -apple-system, sans-serif;
    color: #1c1c1e;
  }
  /*
   * Pin footer to the bottom of a letter content box for short docs.
   * Absolute (not flex+100vh): print iframes and Chrome print ignore viewport flex height.
   */
  .print-page {
    position: relative;
    box-sizing: border-box;
    min-height: 10in; /* 11in letter − 0.5in − 0.5in page margins */
    padding-bottom: 1.5em;
  }
  .guide-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 22px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e24b4a;
  }
  .guide-header > img {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    flex-shrink: 0;
  }
  .guide-header-text {
    flex: 1;
    min-width: 0;
  }
  .guide-header-text h1 {
    font-size: 18px;
    margin: 0;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: #111;
    line-height: 1.25;
  }
  .guide-header-qr {
    margin-left: auto;
    flex-shrink: 0;
    line-height: 0;
  }
  .guide-header-qr img {
    width: 72px;
    height: 72px;
    border-radius: 4px;
    display: block;
  }
  .doc-title {
    font-size: 17px;
    font-weight: 700;
    margin: 0 0 4px;
    color: #111;
    letter-spacing: -0.01em;
  }
  .subtitle {
    font-size: 12.5px;
    color: #666;
    margin: 0 0 12px;
  }
  .pitch {
    font-size: 14px;
    line-height: 1.45;
    color: #2a2a30;
    margin: 0 0 12px;
  }
  h2 {
    font-size: 14.5px;
    margin: 12px 0 4px;
    font-weight: 700;
    color: #185fa5;
    letter-spacing: -0.01em;
    break-after: avoid;
    page-break-after: avoid;
  }
  /* Keep heading with at least the next block (avoids orphaned section titles). */
  .doc-block > h2 + p,
  .doc-block > h2 + table,
  .doc-block > h2 + .roleplay-split,
  .doc-block > h2 + .muted {
    break-before: avoid;
    page-break-before: avoid;
  }
  p, li { font-size: 13px; margin: 0 0 6px; color: #2e2e33; line-height: 1.45; }
  ul, ol { margin: 0 0 8px; padding-left: 1.25em; }
  .section { margin-bottom: 0; }
  .section > p { margin-bottom: 0; }
  .muted { color: #666; }
  .roleplay-split {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 16px;
    align-items: start;
    margin: 6px 0 10px;
  }
  .roleplay-split table { margin: 0; }
  .roleplay-split th, .roleplay-split td { font-size: 12px; }
  /* Right column continues the left — keep column widths, hide header chrome. */
  .roleplay-split > table:last-child thead th {
    color: transparent;
    background: transparent;
    border-bottom-color: transparent;
    user-select: none;
  }
  table.times td:last-child,
  table.topics td:nth-child(2),
  table.topics th:nth-child(2) {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    width: 7.5em;
  }
  table.times th:first-child { width: auto; }
  .feedback {
    margin-top: 14px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #f0f6fc;
    color: #1a3a55;
    font-size: 12.5px;
    line-height: 1.35;
    text-align: center;
  }
  .feedback a {
    color: #185fa5;
    font-weight: 700;
    text-decoration: none;
  }
  .guide-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding-top: 10px;
    border-top: 1px solid #e4e4e8;
    color: #888;
    font-size: 11px;
  }
  table { border-collapse: collapse; width: 100%; margin: 6px 0 10px; }
  th, td {
    border-bottom: 1px solid #e6e6ea;
    text-align: left;
    padding: 5px 7px;
    font-size: 12.5px;
    vertical-align: top;
  }
  tr { break-inside: avoid; page-break-inside: avoid; }
  thead { display: table-header-group; }
  th { color: #111; font-weight: 700; background: #e8e8ec; }
  table.board th:nth-child(1), table.board td:nth-child(1) { width: 18%; }
  table.board th:nth-child(2), table.board td:nth-child(2) { width: 32%; }
  table.board th:nth-child(3), table.board td:nth-child(3) { width: 50%; }
  /* Cheatsheet: dense two-column commercial layout */
  .cheat-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 7px 14px;
    margin: 0;
  }
  .cheat-card {
    break-inside: avoid;
    border: 1px solid #e4e4e8;
    border-radius: 7px;
    padding: 8px 10px 8px;
    background: #fafafb;
  }
  .cheat-card h2 {
    font-size: 11.5px;
    margin: 0 0 6px;
    padding-bottom: 5px;
    border-bottom: 1px solid #d8d8de;
    color: #185fa5;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .cheat-card ul {
    margin: 0;
    padding-left: 1.05em;
  }
  .cheat-card li {
    font-size: 11px;
    margin: 0 0 2px;
    line-height: 1.3;
    color: #2e2e33;
  }
  .cheat-card li b { color: #111; font-weight: 650; }
  .cheat-kbd {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 10px;
    background: #eeeef2;
    border: 1px solid #d0d0d6;
    border-radius: 3px;
    padding: 0 3px;
  }
  @media print {
    .cheat-card { background: #fff; }
    .print-page { min-height: 10in; }
  }
`

export function downloadPdf(docKind: string, bodyHtml: string): void {
  const title = pdfFileTitle(docKind)
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>${PRINT_CSS}</style></head><body>
<div class="print-page">${bodyHtml}</div>
</body></html>`
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  // Letter-sized box so print layout can resolve min-height (0×0 iframes often skip it).
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:8.5in;height:11in;border:0;opacity:0;pointer-events:none'
  document.body.appendChild(iframe)
  const doc = iframe.contentDocument
  if (!doc) {
    iframe.remove()
    return
  }
  doc.open()
  doc.write(html)
  doc.close()
  const padLastPageFooter = () => {
    const page = doc.querySelector('.print-page') as HTMLElement | null
    if (!page) return
    // Content box per letter page (@page margin 0.5in → 10in). Grow to whole pages so the
    // absolute Updated footer sits on the bottom of the last page, not mid-page after content.
    const pageBoxPx = 10 * 96
    const contentH = Math.max(page.scrollHeight, pageBoxPx)
    const pages = Math.max(1, Math.ceil(contentH / pageBoxPx))
    page.style.minHeight = `${pages * 10}in`
  }
  const print = () => {
    try {
      padLastPageFooter()
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } catch {
      /* ignore */
    }
    window.setTimeout(() => iframe.remove(), 30_000)
  }
  if (doc.readyState === 'complete') window.setTimeout(print, 50)
  else iframe.addEventListener('load', () => window.setTimeout(print, 50))
}

export function trainerGuideHtml(): string {
  return `
    ${docHeaderHtml('Trainer Guide')}
    <div class="doc-main">

    <p class="pitch">
      Walk your team through fires, dig-ins, and mutual aid on a shared Map before those moments show up
      in the field. Start from a blank board or a template, tailor a session, <b>Publish</b>, share or invite,
      then <b>Go Live</b> and run the story — then play it back together and talk through what you learned.
    </p>

    <div class="section">
      <h2>What you can do with Scenario Studio</h2>
      <p>
        One tool, a few distinct uses. Pick a <b>session type</b> that matches the job — the board,
        clock, and sidebar tools follow from that.
      </p>
      <ul>
        <li>
          <b>Whiteboard</b> — open map for the situation happening now: drop hazards and zones, talk
          through response, share an incident picture with the team.
        </li>
        <li>
          <b>Walkthrough</b> — present a prepared lesson over Zoom or streaming: you drive Play and Pause
          while others follow the story on the map.
        </li>
        <li>
          <b>Exercise</b> — run a live what-if or re-enactment with named people, roleplay positions,
          Chat, and a shared Run clock.
        </li>
      </ul>
    </div>

    <div class="section">
      <h2>Sessions, Templates, and Getting Started</h2>
      <p>
        Use the <b>Sessions</b> and <b>Templates</b> tabs in the Sidebar. <b>Sessions</b> lists your boards by
        status: Draft, Published, Live, Done, and related groups. <b>Templates</b> is the library of
        example incidents and the blank board. Click a template to preview it on the map, then press
        <b>+</b> to create a session copy. The roster, invites, and run history belong to that copy, not the
        template. Opening Sessions with nothing selected lands on the Sessions hub.
      </p>
      <p>
        First visit, and anytime from Settings, you can open <b>Getting Started</b>: pick
        Whiteboard, Walkthrough, or Exercise, then Set Location. Blank boards get a name
        like <b>Frank&apos;s whiteboard</b>. More scenes live under <b>Templates</b>.
      </p>
      <p>
        Typical flow: <b>Publish</b>, then share or invite, then <b>Go Live</b> when you are ready to start.
      </p>
    </div>

    <div class="section">
      <h2>Map, Time Bar, and Sidebar</h2>
      <p>
        The <b>Map</b> is center stage. The <b>Clock</b> (top) and <b>Time bar</b> (bottom) show
        <b>story time</b>, not wall time, plus Preview, Play or Run, Pause, and <b>Speed</b> when the
        board uses a timed story. <b>Status</b> is Draft, Published, Live, or Done. The <b>Run menu</b>
        shows Previewing, Paused, Running, or Reviewing. Focus hides the Palette, Sidebar, and top bar
        chrome and leaves the Map and the Clock; Esc brings them back. The left <b>Palette</b> places
        symbols. The <b>Sidebar</b> tabs open <b>Timeline</b>, <b>Inspect</b>, <b>Chat</b>, <b>People</b>, and
        <b>Settings</b> as the session type offers them, plus the <b>Sessions</b> and <b>Templates</b> tabs.
        Confirmations open as a <b>panel</b>. <b>Auto-track</b> follows the story camera. A room
        <b>Presenter</b> overrides that while they share. While you follow them, you see plumes the way
        they do. <b>Show plumes</b> in <b>Profile</b> is your own view (<b>W</b>). During a run, a trainer can
        press <b>R</b> — <b>Show wind plumes</b> under <b>This run</b> on the shortcut sheet — to show plumes to everyone.
        The circled
        <b>?</b> in the top bar opens Help (cheatsheet and this guide). Press <b>?</b> for keyboard
        shortcuts. <b>S</b> shares your pointer; click a lineup avatar to choose Presenter.
      </p>
    </div>

    <div class="section">
      <h2>Objects Drive the Scenario</h2>
      <p>
        Place objects from the Palette: Fire, Gas Leak, engines, trucks, and more. Select one, then open
        <b>Inspect</b> to edit name, behavioral properties, weather, timing, and movement. Object properties
        determine when things appear, move, and change; the <b>Timeline</b> narrates that story. Library
        templates are pre-scripted, but you can change them. <b>Wind</b> is a shared board force: set
        direction, speed, and plume shape for a front or storm, and tweak it from Wind or while inspecting
        a plume hazard. Edit the session name and description on the session card or in Settings.
        <b>Background Map</b> picks the map style or operator map for this board, including shared options like
        All Operators when your org enables them.
      </p>
    </div>

    <div class="section">
      <h2>Timeline and Scenario Clock</h2>
      <p>
        Timeline cards are the moments of your story. Hover or select a <b>story card</b>, or scrub the
        <b>Time bar</b>, to jump the <b>playhead</b> and focus it on the Map. In Preview, Reviewing, or a
        Run the Timeline advances with the shared playhead. Story timestamps come from
        <b>Settings → Scenario Story</b> Start / End and the story cards.
        <b>Settings → Schedule</b> Start / End is the planned meeting window (optional).
      </p>
    </div>

    <div class="section">
      <h2>Roster, Roles, and Invites</h2>
      <p>
        Build the <b>Roster</b> on your session copy with the <b>+</b> button, then assign roles for this
        scenario. <b>Roster</b> lists which Buxus users are on this session.
        <b>Owner</b> and <b>Trainer</b> Publish, Go Live, run, and manage the roster; <b>Editor</b> shapes
        the board and can Preview; <b>Participant</b> follows along and may place additional
        objects and can contribute in <b>Chat</b> when that tab is available. When <b>Roleplay</b> is on,
        map roster members to positions that match objects on the board. Use <b>Invite</b>, <b>Nudge</b>,
        <b>Remind</b>, or <b>Invite again</b> so everyone can join.
      </p>
    </div>

    <div class="section">
      <h2>Publish, Go Live, Preview, and Run</h2>
      <p>
        <b>Publish</b> leaves Draft so others can find and join. Share or invite next. <b>Go Live</b> when
        you are ready to start. A <b>Run</b> is the shared exercise — train, discuss, and gather insights
        on one clock. <b>Preview</b> walks through your story during setup — while
        Draft or Published, Owner, Trainer, and Editor share one Preview playhead on the board.
        <b>Play</b> or <b>Start Run</b> advances the story clock after Go Live; <b>Pause</b>,
        <b>Stop</b>, and <b>Start Run</b> N+1 cover the rest of the loop. After a take,
        <b>Reviewing</b> parks at the stop; <b>I&apos;m done reviewing</b> returns to Previewing.
      </p>
    </div>

    <div class="section">
      <h2>Chat and Documents</h2>
      <p>
        <b>Chat</b> has two threads: <b>During this run</b> follows the playhead (script and this take),
        and <b>During this session</b> is the live conversation. A live take shows the run thread only.
        Notes do not carry over when you place the same object type again later. Each note is attributed
        to you and your roleplay position when assigned. Send a <b>Plan</b> PDF beforehand. After the
        session, the <b>After Action Report</b> on the Done session card includes the <b>transcript</b>;
        the <b>Run Summary</b> PDF covers the take. This Trainer Guide and the Product Cheatsheet live
        under the top-bar <b>Help</b> button (circled ?) and can be attached when inviting.
      </p>
    </div>

    <p class="feedback">
      Feedback? <a href="mailto:team@buxus.io">team@buxus.io</a>
    </p>

    </div>
    ${docFooterHtml(TRAINER_GUIDE_UPDATED)}
  `
}

